export type ForwardingLocationInput = {
  warehouseLocation?: { name?: string | null; code?: string | null } | null;
  warehouseArrivalDate?: string | null;
  warehouseDepartureDate?: string | null;
  subAgentArrivalDate?: string | null;
  subAgentDeliveryDate?: string | null;
  vesselAgentArrivalDate?: string | null;
  vesselAgentDeliveryDate?: string | null;
  status?: { name?: string | null } | null;
};

export type ForwardingLocationStage = {
  key: string;
  label: string;
  /** shorter label for badges */
  shortLabel: string;
};

function hasDate(value?: string | null): boolean {
  return Boolean(value?.trim());
}

function warehouseSuffix(row: ForwardingLocationInput): string {
  const wh = row.warehouseLocation;
  if (!wh?.name?.trim()) return '';
  const code = wh.code?.trim();
  return code ? ` (${wh.name} · ${code})` : ` (${wh.name})`;
}

/** Infer current logistics stage from dates (most advanced leg wins). */
export function resolveForwardingLocationStage(row: ForwardingLocationInput): ForwardingLocationStage {
  if (hasDate(row.vesselAgentDeliveryDate)) {
    return {
      key: 'vessel_agent_done',
      label: 'With vessel agent (delivered)',
      shortLabel: 'Vessel agent ✓',
    };
  }
  if (hasDate(row.vesselAgentArrivalDate)) {
    return {
      key: 'vessel_agent',
      label: 'At vessel agent',
      shortLabel: 'Vessel agent',
    };
  }
  if (hasDate(row.subAgentDeliveryDate)) {
    return {
      key: 'sub_agent_done',
      label: 'Sub-agent (delivered onward)',
      shortLabel: 'Sub-agent ✓',
    };
  }
  if (hasDate(row.subAgentArrivalDate)) {
    return {
      key: 'sub_agent',
      label: 'At sub-agent',
      shortLabel: 'Sub-agent',
    };
  }
  if (hasDate(row.warehouseDepartureDate)) {
    return {
      key: 'wh_departed',
      label: 'Left warehouse',
      shortLabel: 'Left WH',
    };
  }
  if (hasDate(row.warehouseArrivalDate)) {
    const suffix = warehouseSuffix(row);
    return {
      key: 'warehouse',
      label: `At warehouse${suffix}`,
      shortLabel: suffix ? `At WH${suffix}` : 'At WH',
    };
  }
  if (row.warehouseLocation) {
    const suffix = warehouseSuffix(row);
    return {
      key: 'warehouse_pending',
      label: `Warehouse assigned${suffix}`,
      shortLabel: suffix ? `WH assigned${suffix}` : 'WH assigned',
    };
  }
  const fallback = row.status?.name?.trim() || 'Not in transit';
  return {
    key: 'not_started',
    label: fallback,
    shortLabel: row.status?.name?.trim() || 'Not started',
  };
}
